import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import logger from '@eneco/common/log';
import { capitalizeFirstLetter, stripAccolades } from '@eneco/common/string';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import postalCodeRegex from '@eneco/common/validation/postalCodeRegex';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { StyledSection } from '@eneco/custom-components/flows/Layout';
import { useFormsPublicV2PostB2BForm } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { Header } from '@eneco/flows/components';
import { chamberOfCommerceSearchMachine } from '@eneco/flows/machines/chamberOfCommerceSearchMachine';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { prepareRevolutionFormRequest } from '@eneco/sitecore-components/forms/GenericForm/util/request';
import {
  Box,
  Heading,
  InputCombobox,
  InputEmail,
  InputTelephone,
  InputText,
  Stack,
  Checkbox,
  Text,
  Button,
  SkipToMain,
} from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { useMachine } from '@xstate/react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

import { AddressFieldsWithAddressFinder } from '../../components/AddressFieldsWithAddressFinder';
import { B2BLeadFlowContext, FixedB2BLeadFlowCompanyDetailsStepRendering, CompanyDetailsFormFields } from '../../types';

export const CompanyDetailsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<B2BLeadFlowContext>();
  const sendToFlowMachine = useFlowActorRef().send;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const flowContext = useFlowSelector(state => state.context);

  const { businessUnit, label } = useDC();
  const { isError, isSuccess, isLoading, send, error: b2bFormRequestError } = useFormsPublicV2PostB2BForm();

  const {
    firstName,
    lastName,
    companyName,
    emailAddress,
    phoneNumber,
    postalCode,
    houseNumber,
    houseNumberSuffix,
    selectedProducts,
  } = flowContext;
  const [chamberOfCommerceSearchState, sendToChamberOfCommerceSearchMachine] =
    useMachine(chamberOfCommerceSearchMachine);
  const { context: { results } = {} } = chamberOfCommerceSearchState;
  const companyNameOptions = results?.map(result => result?.name ?? '') || [];

  const { B2BLeadFlowCompanyDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    B2BLeadFlowCompanyDetailsStep: FixedB2BLeadFlowCompanyDetailsStepRendering;
  }>();

  // Note: the form field textual data should be added in the sitecore template.
  // After the sitecore template updated, the reference should be fixed.
  const firstNameFormField = textualData?.personalDetails?.initialsFormField;

  const formSchema = yup.object({
    recaptchaToken: yup.string().required(),
    firstName: yup
      .string()
      .required(firstNameFormField?.value?.requiredMessage)
      .matches(/^(?=.*[\w\s])[^\w]*.*$/, firstNameFormField?.value?.validationMessage),
    lastName: yup.string().required(textualData?.personalDetails?.surnameFormField?.value?.requiredMessage),
    postalCode: yup.string().required('Postcode is verplicht').matches(postalCodeRegex, 'Vul een geldige postcode in'),
    houseNumber: yup.number().required('Huisnummer is verplicht').typeError('Vul een geldig huisnummer in'),
    houseNumberSuffix: yup.string().optional(),
    companyName: yup.string().required(textualData?.companyDetails?.companyNameFormField?.value?.requiredMessage),
    chamberOfCommerceNumber: yup
      .string()
      .required(textualData?.companyDetails?.chamberOfCommerceNumberFormField?.value?.requiredMessage),
    phoneNumber: yup
      .string()
      .required(textualData?.contactDetails?.phoneNumberFormField?.value?.requiredMessage)
      .matches(
        /^(?:0[0-57-9]\d{2}[-\s]?\d{6}|0[0-57-9]\d[-\s]?\d{7}|06[-\s]?\d{8})$/,
        textualData?.contactDetails?.phoneNumberFormField?.value?.validationMessage,
      ),
    emailAddress: emailValidation({
      emailMessage: textualData?.contactDetails?.emailAddressFormField?.value?.validationMessage,
      requiredMessage: textualData?.contactDetails?.emailAddressFormField?.value?.requiredMessage,
    }),
    newsletterConsent: yup.boolean(),
    weeklyVisionConsent: yup.boolean(),
    city: yup.string(),
    street: yup.string(),
    privacyConsent: yup
      .boolean()
      .oneOf([true], textualData?.consents?.privacyConsentInputField?.value?.requiredMessage),
  });

  const methods = useForm<CompanyDetailsFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      firstName,
      lastName,
      postalCode,
      houseNumber,
      houseNumberSuffix: houseNumberSuffix ?? undefined,
      companyName,
      emailAddress,
      phoneNumber,
    },
    resolver: typedYupResolver<CompanyDetailsFormFields>(formSchema),
  });

  const {
    getValues,
    watch,
    setValue,
    register,
    trigger,
    getFieldState,
    formState: { errors, isValidating },
  } = methods;
  const watchCompanyName = watch('companyName');

  const selectedChamberOfCommerceNumber =
    results?.find(result => result.name === watchCompanyName)?.chamberOfCommerceNumber ?? '';

  useDebounce(
    () => {
      const isCompanyNameInOptions = companyNameOptions.includes(watchCompanyName);
      if (watchCompanyName && !isCompanyNameInOptions) {
        sendToChamberOfCommerceSearchMachine({
          type: 'GET_COMPANY_OPTIONS',
          config: { businessUnit, label },
          values: {
            companyName: watchCompanyName,
          },
        });
      }
    },
    500,
    [watchCompanyName],
  );

  useEffect(() => {
    if (selectedChamberOfCommerceNumber) {
      setValue('chamberOfCommerceNumber', selectedChamberOfCommerceNumber);
      trigger('companyName');
    }
  }, [selectedChamberOfCommerceNumber, setValue, trigger]);

  const getFormatedValues = () => {
    const subscriptionKey = 'msdynmkt_purposeid;channels;optinwhenchecked;msdynmkt_value';
    const values = getValues();
    const subject = 'Sales One Form';

    const data: { [key: string]: string } = {
      leadsourcecode: textualData?.settings?.leadSourceCodeText?.value || '',
      enc_formtype: textualData?.settings?.encFormTypeText?.value || '',
      firstname: values.firstName,
      lastname: values.lastName,
      companyname: values.companyName,
      wsnl_kvkno: values.chamberOfCommerceNumber,
      address1_postalcode: values.postalCode,
      address1_line2: `${values.houseNumber}`,
      address1_line3: values.houseNumberSuffix ?? '',
      mobilephone: values.phoneNumber,
      emailaddress1: values.emailAddress,
      enc_slimterugleveren: selectedProducts['enc_slimterugleveren'],
      enc_batterij: selectedProducts['enc_batterij'],
      enc_slimmeenergiesturing: selectedProducts['enc_slimmeenergiesturing'],
      enc_elektriciteitgas: selectedProducts['enc_elektriciteitgas'],
      enc_warmtepomp: selectedProducts['enc_warmtepomp'],
      enc_zonnepanelen: selectedProducts['enc_zonnepanelen'],
      enc_vastgoedverduurzamen: selectedProducts['enc_vastgoedverduurzamen'],
      enc_elektrischladen: selectedProducts['enc_elektrischladen'],
      enc_ietsanders: selectedProducts['enc_ietsanders'],
      description: flowContext.otherRequestDescription,
      subject,
    };
    if (values.newsletterConsent) {
      data[`${subscriptionKey};unique_key_000`] =
        textualData?.settings?.newsletterConsentIdOption?.fields.data.value.value ?? '';
    }
    if (values.weeklyVisionConsent) {
      data[`${subscriptionKey};unique_key_001`] =
        textualData?.settings?.weeklyVisionConsentIdOption?.fields.data.value.value ?? '';
    }
    return data;
  };

  const kvkNumberOutput = selectedChamberOfCommerceNumber && (
    <Heading as="h3" size="2XS">
      {textualData?.companyDetails?.chamberOfCommerceNumberFormField?.value?.label}: {selectedChamberOfCommerceNumber}
    </Heading>
  );

  const recaptchaRef = useRef<RecaptchaRef>(null);

  const submitForm: SubmitHandler<CompanyDetailsFormFields> = values => {
    setIsSubmitting(true);

    const formId = textualData?.settings?.senderIdOption?.fields.data.value.value ?? '';
    const requestBody = prepareRevolutionFormRequest(formId, getFormatedValues());

    send({
      businessUnit,
      formId: stripAccolades(formId),
      label,
      requestBody,
      recaptchaEnterpriseToken: values.recaptchaToken,
    } as any); // eslint-disable-line @typescript-eslint/no-explicit-any
  };

  const onFormSubmitSuccess = () => {
    const values = getValues();

    sendToFlowMachine({
      type: 'NEXT_PAGE',
      values: {
        ...flowContext,
        firstName: values.firstName,
        lastName: values.lastName,
        postalCode: values.postalCode,
        houseNumber: Number(values.houseNumber),
        houseNumberSuffix: values.houseNumberSuffix || undefined,
        companyName: values.companyName,
        chamberOfCommerceNumber: values.chamberOfCommerceNumber,
        phoneNumber: values.phoneNumber,
        emailAddress: values.emailAddress,
      },
    });
  };

  useEffect(() => {
    if (isSuccess) {
      onFormSubmitSuccess();
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      logger.error('BbpwR2', 'Failed to submit the form', { error: b2bFormRequestError || '' });
      setIsSubmitting(false);
    }
  }, [isError, b2bFormRequestError]);

  const formOutput = (
    <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
      <Stack direction="column" gap="6">
        <Header>
          <Heading as="h1" size="L">
            {textualData?.content?.title?.value}
          </Heading>
          {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
        </Header>

        <Box paddingBottom={4}>
          <Stack direction="column" gap={10}>
            <Stack direction="column" gap={6}>
              <InputText
                {...register('firstName', {
                  onBlur: (event: ChangeEvent<HTMLInputElement>) => {
                    setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
                  },
                })}
                label={firstNameFormField?.value?.label ?? ''}
                placeholder={firstNameFormField?.value?.placeholder}
                hint={firstNameFormField?.value?.hint}
                error={errors.firstName?.message}
              />
              <InputText
                {...register('lastName', {
                  onBlur: (event: ChangeEvent<HTMLInputElement>) => {
                    setValue('lastName', capitalizeFirstLetter(event?.target?.value?.trim()));
                  },
                })}
                label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
                placeholder={textualData?.personalDetails?.surnameFormField?.value?.placeholder}
                hint={textualData?.personalDetails?.surnameFormField?.value?.hint}
                error={errors.lastName?.message}
              />

              <Stack direction="column" gap={6}>
                <InputCombobox
                  {...register('companyName')}
                  defaultValue={companyName}
                  onChange={value => {
                    const newValue = value.trim();
                    if (newValue !== watchCompanyName) {
                      setValue('companyName', newValue);
                    }
                  }}
                  label={textualData?.companyDetails?.companyNameFormField?.value?.label ?? ''}
                  placeholder={textualData?.companyDetails?.companyNameFormField?.value?.placeholder}
                  hint={textualData?.companyDetails?.companyNameFormField?.value?.hint}
                  error={errors.companyName?.message}
                  options={
                    chamberOfCommerceSearchState.matches('FETCHING')
                      ? [textualData?.content?.companySearchLoadingText?.value ?? '']
                      : companyNameOptions
                  }
                />
                {kvkNumberOutput}
              </Stack>

              <AddressFieldsWithAddressFinder
                register={register}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                getFieldState={getFieldState}
                isValidating={isValidating}
                defaultValues={{
                  postalCode,
                  houseNumber,
                  houseNumberSuffix: houseNumberSuffix ?? undefined,
                }}
              />

              <InputEmail
                {...register('emailAddress')}
                label={textualData?.contactDetails?.emailAddressFormField?.value?.label ?? ''}
                placeholder={textualData?.contactDetails?.emailAddressFormField?.value?.placeholder}
                hint={textualData?.contactDetails?.emailAddressFormField?.value?.hint}
                autoComplete="email"
                error={errors.emailAddress?.message}
              />
              <InputTelephone
                {...register('phoneNumber')}
                label={textualData?.contactDetails?.phoneNumberFormField?.value?.label ?? ''}
                placeholder={textualData?.contactDetails?.phoneNumberFormField?.value?.placeholder}
                hint={textualData?.contactDetails?.phoneNumberFormField?.value?.hint}
                autoComplete="tel"
                error={errors.phoneNumber?.message}
              />
            </Stack>
            <Stack direction="column" gap="4">
              <Heading size="3XS" as={'h2'}>
                Op de hoogte blijven van de nieuwste ontwikkelingen?
              </Heading>
              <Stack.Item>
                <Checkbox
                  weight={'bold'}
                  label={textualData?.consents?.newsletterConsentInputField?.value?.label ?? ''}
                  {...register('newsletterConsent')}
                />
                <Box paddingLeft={7} paddingTop={1}>
                  <RichText html={textualData?.consents?.newsletterConsentInputField?.value?.hint ?? ''} />
                </Box>
              </Stack.Item>
              <Stack.Item>
                <Checkbox
                  weight={'bold'}
                  label={textualData?.consents?.weeklyVisionConsentInputField?.value?.label ?? ''}
                  {...register('weeklyVisionConsent')}
                />
                <Box paddingLeft={7}>
                  <RichText html={textualData?.consents?.weeklyVisionConsentInputField?.value?.hint ?? ''} />
                </Box>
              </Stack.Item>

              <Checkbox
                {...register('privacyConsent')}
                weight={'bold'}
                label={textualData?.consents?.privacyConsentInputField?.value?.label ?? ''}
                error={errors.privacyConsent?.message}
              />

              <RichText html={textualData?.consents?.privacyConsentInputField?.value?.hint ?? ''} />
            </Stack>
          </Stack>
        </Box>
        <Text size="BodyS">
          <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
        </Text>
        {isError && textualData?.content?.notification?.value?.content && (
          <TrackedNotificationBox
            label={textualData?.content?.notification?.value?.content}
            isAlert
            text={textualData?.content?.notification?.value?.content}
            variant="error"
          />
        )}
        <Stack.Item>
          <Button type="submit" isLoading={isSubmitting || isLoading}>
            {textualData?.content?.nextStepText?.value}
          </Button>
        </Stack.Item>
      </Stack>
    </RecaptchaForm>
  );

  return (
    <SkipToMain.Main>
      <StyledSection variant="B" withDelayedAnimation={false}>
        <Box paddingY={{ initial: '8', md: '12', xl: '16' }} paddingX={{ initial: '6', md: '10', lg: '0' }}>
          <FormProvider {...methods}>{formOutput}</FormProvider>
        </Box>
      </StyledSection>
    </SkipToMain.Main>
  );
};
